import { useState, useEffect } from 'react';

import { AppIcon, InfoModal, ConfirmDelete } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useCreateShareSessionMutation,
  useRevokeShareSessionMutation,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession.api';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, Typography, TextField, Button, SxProps, Theme, Tooltip } from '@mui/material';

const STRINGS = appStrings.modules.shareResourceModal;

type ShareResourceModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  resourceType: 'blueprint' | 'solution_session' | 'office_hours';
  resourceId?: number;
  sharePath: string;
  resourceName: string;
}>;

export const ShareResourceModal = ({
  open,
  onClose,
  resourceType,
  resourceId,
  sharePath,
  resourceName,
}: ShareResourceModalProps) => {
  const [createShareSessionTrigger, { isLoading: isCreating }] = useCreateShareSessionMutation();
  const [revokeShareSessionTrigger, { isLoading: isRevoking }] = useRevokeShareSessionMutation();
  const [shareSessionId, setShareSessionId] = useState<string | null>(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const isLoading = isCreating || isRevoking;

  useEffect(() => {
    if (open && !shareSessionId) {
      const createSession = async () => {
        try {
          const result = await createShareSessionTrigger({
            resourceType,
            resourceId,
          }).unwrap();

          setShareSessionId(result.share_session_id);
        } catch (error) {
          console.error('Failed to create share session:', error);
        }
      };

      createSession();
    }
  }, [open, shareSessionId, resourceType, resourceId, createShareSessionTrigger]);

  useEffect(() => {
    if (!open) {
      setShareSessionId(null);
    }
  }, [open]);

  const shareLink = shareSessionId ? `${GROW_URL}/shared/${shareSessionId}${sharePath}` : '';

  const handleCopyLink = async () => {
    if (!shareLink) return;

    try {
      await navigator.clipboard.writeText(shareLink);
      setShowCopySuccess(true);

      setTimeout(() => {
        setShowCopySuccess(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleRevokeSession = async () => {
    try {
      await revokeShareSessionTrigger({
        resourceType,
        resourceId,
      }).unwrap();
      setShareSessionId(null);
      setShowConfirmDelete(false);
      onClose();
    } catch (error) {
      console.error('Failed to revoke share session:', error);
    }
  };

  const handleTrashClick = () => {
    setShowConfirmDelete(true);
  };

  return (
    <>
      <InfoModal
        open={open}
        width={false}
        onClose={onClose}
        dialogSx={dialogSx}
        featuredIcon={{
          iconName: 'Share06',
          size: 'lg',
          color: 'Brand/500',
          shape: 'round',
          backgroundColor: 'Colors/Background/bg-brand-secondary',
          border: false,
        }}
        headerContent={
          <Box sx={headerContentSx}>
            <Typography
              variant="text-lg-semibold"
              color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
            >
              {STRINGS.title(resourceName)}
            </Typography>
            <Typography
              variant="text-sm-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
            >
              {STRINGS.description}
            </Typography>
          </Box>
        }
        bodyContent={
          <Box sx={shareModalBodySx}>
            <Box>
              <Typography
                variant="text-sm-medium"
                color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
              >
                {STRINGS.shareLinkLabel}
              </Typography>
              <Box sx={shareLinkContainerSx}>
                <TextField
                  id="share-resource-link-input"
                  fullWidth
                  value={shareLink}
                  InputProps={{ readOnly: true }}
                />
                <Box
                  id="share-resource-revoke-button"
                  sx={trashIconContainerSx}
                  onClick={handleTrashClick}
                >
                  <AppIcon
                    iconName="Trash01"
                    diameter="20px"
                    color="Colors/Foreground/fg-quaternary"
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={shareModalActionsSx}>
              <Button
                id="share-resource-cancel-button"
                variant="contained"
                size="md"
                hierarchy="Secondary gray"
                onClick={onClose}
                sx={{ width: '100%' }}
                disabled={isLoading}
              >
                {STRINGS.cancelButton}
              </Button>
              <Tooltip
                open={showCopySuccess}
                title={STRINGS.copiedTooltip}
                placement="top"
                arrow
                disableFocusListener
                disableHoverListener
                disableTouchListener
                componentsProps={{
                  popper: {
                    sx: linkCopiedPopperSx,
                  },
                }}
              >
                <Button
                  id="share-resource-copy-link-button"
                  variant="contained"
                  size="md"
                  hierarchy="Primary"
                  onClick={handleCopyLink}
                  disabled={isLoading || !shareSessionId}
                  sx={{ width: '100%' }}
                >
                  {STRINGS.copyLinkButton}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        }
      />
      <ConfirmDelete
        openDialog={showConfirmDelete}
        setOpenDialog={setShowConfirmDelete}
        headerText={STRINGS.confirmDelete.title(resourceName)}
        bodyText={STRINGS.confirmDelete.description}
        handleDelete={handleRevokeSession}
        headerInLineWithIcon={false}
      />
    </>
  );
};

const shareModalBodySx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const shareLinkContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
  alignItems: 'center',
  mb: backrs.spacing['spacing-3xl'],
  mt: backrs.spacing['spacing-sm'],
});

const shareModalActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const trashIconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  ml: backrs.spacing['spacing-xs'],
});

const linkCopiedPopperSx: SxProps<Theme> = {
  zIndex: 99999999999999,
};

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  position: 'relative',
  zIndex: 2,
});

const dialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    maxWidth: '512px',
  },
};
